import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { playerPosition } from "./controls";
import { playChirp } from "./audio";

interface BirdProps {
  center: [number, number, number];
  radius: number;
  height: number;
  speed: number;
  phase: number;
}

const CHIRP_RADIUS = 14;

/** A small decorative bird that circles at height, flapping its wings. */
const Bird: React.FC<BirdProps> = ({ center, radius, height, speed, phase }) => {
  const groupRef = useRef<THREE.Group>(null);
  const leftWingRef = useRef<THREE.Mesh>(null);
  const rightWingRef = useRef<THREE.Mesh>(null);
  const chirpTimer = useRef(2 + Math.random() * 4);

  useFrame(({ clock }, delta) => {
    const t = clock.getElapsedTime() * speed + phase;
    const x = center[0] + Math.sin(t) * radius;
    const z = center[2] + Math.cos(t) * radius;
    const y = height + Math.sin(t * 2) * 0.3;
    if (groupRef.current) {
      groupRef.current.position.set(x, y, z);
      const dx = Math.cos(t) * radius;
      const dz = -Math.sin(t) * radius;
      groupRef.current.rotation.y = Math.atan2(-dx, -dz);
    }
    const flap = Math.sin(clock.getElapsedTime() * 14 + phase) * 0.7;
    if (leftWingRef.current) leftWingRef.current.rotation.z = flap;
    if (rightWingRef.current) rightWingRef.current.rotation.z = -flap;

    chirpTimer.current -= delta;
    if (chirpTimer.current <= 0) {
      chirpTimer.current = 3 + Math.random() * 5;
      if (groupRef.current && playerPosition.distanceTo(groupRef.current.position) < CHIRP_RADIUS) {
        playChirp();
      }
    }
  });

  return (
    <group ref={groupRef} scale={0.4}>
      <mesh castShadow>
        <boxGeometry args={[0.2, 0.16, 0.42]} />
        <meshStandardMaterial color="#3a3a3a" roughness={0.8} />
      </mesh>
      <mesh position={[0, 0.05, -0.26]}>
        <boxGeometry args={[0.12, 0.12, 0.12]} />
        <meshStandardMaterial color="#3a3a3a" roughness={0.8} />
      </mesh>
      <mesh ref={leftWingRef} position={[-0.14, 0.02, 0]}>
        <boxGeometry args={[0.5, 0.03, 0.22]} />
        <meshStandardMaterial color="#565656" roughness={0.8} />
      </mesh>
      <mesh ref={rightWingRef} position={[0.14, 0.02, 0]}>
        <boxGeometry args={[0.5, 0.03, 0.22]} />
        <meshStandardMaterial color="#565656" roughness={0.8} />
      </mesh>
    </group>
  );
};

export default Bird;
